import { MISSING_VALUE } from '../../../../constants'
import { LocationFeature } from '../../../../api/models'
import { FEATURE_NAME_MAP, NO_ODMON_CAMERAS_ENROLLED_NOTE, TWO_WAY_AUDIO_ESCALATION_ONLY_FEATURE_NAME } from './constants'

export type SopFeatureRow = LocationFeature & { note?: string }

export function pascalCaseToSentenceCase(value: string): string {
  return value.replace(/([a-z0-9])([A-Z])/g, '$1 $2')
}

export function getFeatureDisplayName(name?: string): string {
  if (!name) {
    return MISSING_VALUE
  }

  return FEATURE_NAME_MAP[name] || pascalCaseToSentenceCase(name)
}

export function getFeatureNameTestId(name?: string): string {
  if (!name) {
    return 'feature-name-missing'
  }

  return `feature-name-${name.replace(/[^a-zA-Z0-9]+/g, '-')}`
}

/**
 * Synthesizes an "Enabled: No" row for the 2WA SOP feature when the location hasn't made an explicit
 * SOP choice yet (the flag is absent from `features`). The feature only applies to locations on an
 * ODMON-eligible plan at all, so it's stripped entirely when the plan isn't eligible, regardless of
 * what's recorded in `features`. On an eligible plan with no ODMON-enrolled camera, the row is kept
 * (rather than omitted) with a `note` explaining why the setting can't currently take effect.
 */
export function withSopFeatureRow(
  features: LocationFeature[],
  hasOdmonEnrolledCamera: boolean,
  hasEligiblePlan: boolean | undefined
): SopFeatureRow[] {
  if (!hasEligiblePlan) {
    return features.filter(feature => feature.name !== TWO_WAY_AUDIO_ESCALATION_ONLY_FEATURE_NAME)
  }

  const explicitSopFeature = features.find(feature => feature.name === TWO_WAY_AUDIO_ESCALATION_ONLY_FEATURE_NAME)

  if (hasOdmonEnrolledCamera) {
    return explicitSopFeature ? features : [...features, { name: TWO_WAY_AUDIO_ESCALATION_ONLY_FEATURE_NAME, enabled: false }]
  }

  const noCamerasEnrolledRow: SopFeatureRow = {
    ...(explicitSopFeature ?? { name: TWO_WAY_AUDIO_ESCALATION_ONLY_FEATURE_NAME, enabled: false }),
    note: NO_ODMON_CAMERAS_ENROLLED_NOTE
  }

  return explicitSopFeature
    ? features.map(feature => (feature === explicitSopFeature ? noCamerasEnrolledRow : feature))
    : [...features, noCamerasEnrolledRow]
}
